/*
 * TGC — cart drawer upsell rail.
 *
 * Adds from the rail in sections/cart-drawer-recommendations.liquid without opening
 * Dawn's quick-add modal: the card's "+ Add" flips into size chips, and picking one
 * posts to /cart/add and hands the response to the cart drawer to re-render.
 *
 * Everything is delegated off `document` on purpose. The drawer replaces the whole of
 * .drawer__inner on every cart update, and the rail itself is injected later still by
 * the product-recommendations element, so anything bound to those nodes directly would
 * be thrown away on the first add.
 */
(function () {
  'use strict';

  var SCROLL_NUDGE = 2; // px of slack, so a sub-pixel scrollLeft doesn't read as "not at the end"
  var DISMISS_KEY = 'tgc:cart-upsell-dismissed';

  /*
   * Dismissal is per browsing session, not permanent: someone who doesn't want
   * suggestions today shouldn't lose them forever. sessionStorage throws outright in
   * some privacy modes, so every access is guarded — a browser that refuses to store
   * simply gets the rail back, which is the safe way to fail.
   *
   * The drawer rebuilds .drawer__inner on every cart change, so the flag has to be
   * re-applied to each freshly rendered rail, not just the one that was clicked.
   */
  function isDismissed() {
    try {
      return sessionStorage.getItem(DISMISS_KEY) === '1';
    } catch (error) {
      return false;
    }
  }

  function rememberDismissal() {
    try {
      sessionStorage.setItem(DISMISS_KEY, '1');
    } catch (error) {
      /* storage unavailable — the rail stays hidden for this render only */
    }
  }

  function applyDismissal() {
    if (!isDismissed()) return;
    document.querySelectorAll('.tgc-upsell').forEach(function (section) {
      section.classList.add('tgc-upsell--dismissed');
    });
  }

  function drawer() {
    return document.querySelector('cart-drawer');
  }

  function rail(fromNode) {
    var section = fromNode.closest('.tgc-upsell') || document;
    return section.querySelector('[data-tgc-upsell-rail]');
  }

  /* ---------- arrows ---------- */

  function syncArrows() {
    document.querySelectorAll('.tgc-upsell').forEach(function (section) {
      var scroller = section.querySelector('[data-tgc-upsell-rail]');
      var prev = section.querySelector('[data-tgc-upsell-prev]');
      var next = section.querySelector('[data-tgc-upsell-next]');
      if (!scroller || !prev || !next) return;

      var max = scroller.scrollWidth - scroller.clientWidth;
      prev.disabled = scroller.scrollLeft <= SCROLL_NUDGE;
      next.disabled = scroller.scrollLeft >= max - SCROLL_NUDGE;
    });
  }

  function step(scroller) {
    var card = scroller.querySelector('[data-tgc-upsell-card]');
    if (!card) return scroller.clientWidth;
    var gap = parseFloat(getComputedStyle(scroller).columnGap) || 0;
    return card.getBoundingClientRect().width + gap;
  }

  /* ---------- add to cart ---------- */

  function setError(card, message) {
    var slot = card.querySelector('[data-tgc-upsell-error]');
    if (!slot) return;
    slot.textContent = message || '';
    slot.hidden = !message;
  }

  function closeSizes(card) {
    var sizes = card.querySelector('[data-tgc-upsell-sizes]');
    var add = card.querySelector('[data-tgc-upsell-add]');
    if (sizes) sizes.hidden = true;
    if (add) add.setAttribute('aria-expanded', 'false');
    card.classList.remove('tgc-upsell__card--picking');
  }

  function openSizes(card) {
    document.querySelectorAll('.tgc-upsell__card--picking').forEach(function (other) {
      if (other !== card) closeSizes(other);
    });

    var sizes = card.querySelector('[data-tgc-upsell-sizes]');
    var add = card.querySelector('[data-tgc-upsell-add]');
    if (!sizes) return;

    sizes.hidden = false;
    if (add) add.setAttribute('aria-expanded', 'true');
    card.classList.add('tgc-upsell__card--picking');
    setError(card, '');

    var firstChip = sizes.querySelector('button:not([disabled])');
    if (firstChip) firstChip.focus();
  }

  function addToCart(card, variantId, trigger) {
    var cart = drawer();
    if (!cart) {
      window.location = window.routes.cart_url;
      return;
    }

    card.classList.add('tgc-upsell__card--loading');
    if (trigger) trigger.setAttribute('aria-disabled', 'true');
    setError(card, '');

    var body = new FormData();
    body.append('id', variantId);
    body.append('quantity', 1);
    body.append('sections', cart.getSectionsToRender().map(function (s) { return s.id; }).join(','));
    body.append('sections_url', window.location.pathname);

    cart.setActiveElement(document.activeElement);

    fetch(window.routes.cart_add_url, {
      method: 'POST',
      headers: { Accept: 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' },
      body: body
    })
      .then(function (response) { return response.json(); })
      .then(function (response) {
        if (response.status) {
          setError(card, response.description || response.message);
          return;
        }

        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'tgc-cart-upsell',
            productVariantId: variantId,
            cartData: response
          });
        }

        // Re-renders .drawer__inner, which takes this rail with it — nothing left to reset.
        cart.renderContents(response);
      })
      .catch(function (error) {
        console.error(error);
        setError(card, window.tgcUpsellErrorText || 'Could not add. Try again.');
      })
      .finally(function () {
        card.classList.remove('tgc-upsell__card--loading');
        if (trigger) trigger.removeAttribute('aria-disabled');
      });
  }

  /* ---------- delegation ---------- */

  document.addEventListener('click', function (event) {
    var prev = event.target.closest('[data-tgc-upsell-prev]');
    var next = event.target.closest('[data-tgc-upsell-next]');

    if (prev || next) {
      var scroller = rail(prev || next);
      if (!scroller) return;
      scroller.scrollBy({ left: next ? step(scroller) : -step(scroller), behavior: 'smooth' });
      return;
    }

    if (event.target.closest('[data-tgc-upsell-dismiss]')) {
      rememberDismissal();
      applyDismissal();
      return;
    }

    var card = event.target.closest('[data-tgc-upsell-card]');

    if (!card) {
      document.querySelectorAll('.tgc-upsell__card--picking').forEach(closeSizes);
      return;
    }

    var chip = event.target.closest('[data-tgc-upsell-sizes] [data-variant-id]');
    if (chip) {
      addToCart(card, chip.dataset.variantId, chip);
      return;
    }

    var add = event.target.closest('[data-tgc-upsell-add]');
    if (!add || add.getAttribute('aria-disabled') === 'true') return;

    if (add.dataset.variantId) {
      addToCart(card, add.dataset.variantId, add);
    } else if (card.classList.contains('tgc-upsell__card--picking')) {
      closeSizes(card);
    } else {
      openSizes(card);
    }
  });

  document.addEventListener('keyup', function (event) {
    if (event.code !== 'Escape') return;
    var card = event.target.closest('.tgc-upsell__card--picking');
    if (!card) return;

    // Stop the drawer's own Escape handler from closing the whole cart behind us.
    event.stopPropagation();
    closeSizes(card);
    var add = card.querySelector('[data-tgc-upsell-add]');
    if (add) add.focus();
  }, true);

  document.addEventListener('scroll', function (event) {
    if (event.target.matches && event.target.matches('[data-tgc-upsell-rail]')) syncArrows();
  }, true);

  window.addEventListener('resize', syncArrows);

  /* The rail arrives async — once from the recommendations fetch, again after every
     cart update re-renders the drawer. Watch the drawer rather than polling. */
  function observe() {
    var cart = drawer();
    if (!cart) return;
    new MutationObserver(onDrawerRender).observe(cart, { childList: true, subtree: true });

    // The drawer announces itself by gaining .active; that's our cue to fetch.
    new MutationObserver(onDrawerRender).observe(cart, {
      attributes: true,
      attributeFilter: ['class']
    });

    onDrawerRender();
  }

  function onDrawerRender() {
    applyDismissal();
    ensureRailLoaded();
    syncArrows();
  }

  /*
   * The rail now sits inside cart-drawer-items, the scrolling region. Dawn's
   * product-recommendations element waits for an IntersectionObserver before fetching,
   * and an element clipped by a scroll container never reports as intersecting — so
   * below-the-fold suggestions would stay empty no matter how far you scrolled.
   *
   * Load it ourselves once the drawer is actually open. Not on page load: an unopened
   * drawer shouldn't cost a request. Dawn's own observer is disconnected first so the
   * two can't both fire and fetch twice.
   */
  function ensureRailLoaded() {
    if (isDismissed()) return;

    var cart = drawer();
    if (!cart || !cart.classList.contains('active')) return;

    var loader = cart.querySelector('.tgc-upsell');
    if (!loader || loader.dataset.tgcLoading || loader.innerHTML.trim().length) return;
    if (typeof loader.loadRecommendations !== 'function') return;

    loader.dataset.tgcLoading = '1';
    if (loader.observer) loader.observer.disconnect();
    loader.loadRecommendations(loader.dataset.productId);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', observe);
  } else {
    observe();
  }
})();
